/**
 * Canvas Data Fetching Hooks
 *
 * Hooks for fetching and managing canvas applications.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createViewService } from "../services/viewService";
import {
  loadViewDefinitionForObjectChange,
  viewUsesTable,
} from "./useViewDataQuery";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import type {
  Canvas,
  CanvasCreate,
  CanvasUpdate,
  CanvasData,
  CanvasSubmitResult,
} from "../types";
import {
  subscribeWorkspaceObjectChanges,
  workspaceChangeAffects,
} from "../services/workspaceObjectChanges";

const CANVAS_API_BASE = "/api/workspace/myvault/canvases";

/**
 * Helper to create headers with workspace ID
 */
const getWorkspaceHeaders = (workspaceId: string): Record<string, string> => ({
  "x-workspace-id": workspaceId,
});

/**
 * Hook to fetch all canvases for a workspace.
 */
export const useCanvases = (
  workspaceId: string,
  options?: { type?: string; status?: string; includeUnpublished?: boolean },
) => {
  const [canvases, setCanvases] = useState<Canvas[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchCanvases = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (options?.type) params.set("type", options.type);
      if (options?.status) params.set("status", options.status);
      if (options?.includeUnpublished === false)
        params.set("include_unpublished", "false");

      const queryString = params.toString();
      const url = `${CANVAS_API_BASE}${queryString ? `?${queryString}` : ""}`;

      const response = await authFetchRef.current(url, "fetch canvases", {
        headers: getWorkspaceHeaders(workspaceId),
      });
      if (!signal.aborted) {
        setCanvases(response.items || response || []);
        setError(null);
      }
    } catch (err) {
      if (!signal.aborted) {
        console.error("Canvases fetch error:", err);
        const message =
          err instanceof Error ? err.message : "Failed to fetch canvases";
        setError(message);
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [
    workspaceId,
    options?.type,
    options?.status,
    options?.includeUnpublished,
  ]);

  useEffect(() => {
    fetchCanvases();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchCanvases]);

  useEffect(
    () =>
      subscribeWorkspaceObjectChanges(workspaceId, (change) => {
        if (workspaceChangeAffects(change, ["canvas"])) fetchCanvases();
      }),
    [workspaceId, fetchCanvases],
  );

  return {
    canvases,
    loading,
    error,
    refresh: fetchCanvases,
  };
};

/**
 * Hook to fetch a single canvas by ID.
 */
export const useCanvas = (
  workspaceId: string,
  canvasId: number | string | null,
) => {
  const [canvas, setCanvas] = useState<Canvas | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchCanvas = useCallback(async () => {
    if (!workspaceId || canvasId === null) {
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    try {
      setLoading(true);
      // Use /full endpoint to get canvas with all nested data (elements, data sources, etc.)
      const response = await authFetchRef.current(
        `${CANVAS_API_BASE}/${canvasId}/full`,
        "fetch canvas",
        { headers: getWorkspaceHeaders(workspaceId) },
      );
      if (!signal.aborted) {
        setCanvas(response);
        setError(null);
        setErrorStatus(null);
        return response;
      }
    } catch (err) {
      if (!signal.aborted) {
        console.error("Canvas fetch error:", err);
        setCanvas(null);
        setErrorStatus(
          typeof (err as { status?: unknown })?.status === "number"
            ? (err as { status: number }).status
            : null,
        );
        const message =
          err instanceof Error ? err.message : "Failed to fetch canvas";
        setError(message);
      }
      return null;
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [workspaceId, canvasId]);

  useEffect(() => {
    fetchCanvas();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchCanvas]);

  useEffect(
    () =>
      subscribeWorkspaceObjectChanges(workspaceId, (change) => {
        if (workspaceChangeAffects(change, ["canvas"], canvasId)) fetchCanvas();
      }),
    [workspaceId, canvasId, fetchCanvas],
  );

  return {
    canvas,
    loading,
    error,
    errorStatus,
    refresh: fetchCanvas,
  };
};

/**
 * Hook to fetch canvas data for runtime execution.
 * Includes the canvas definition, bound data, and permissions.
 */
export const useCanvasData = (
  workspaceId: string,
  canvasId: number | string | null,
  rowId?: string | number,
  dataSourceAlias?: string,
) => {
  const [data, setData] = useState<CanvasData | null>(null);
  // The row id and data source alias the current `data` was fetched for, so a caller can
  // tell it from the previous query's data that stays in place until a refetch lands.
  const [loadedFor, setLoadedFor] = useState<{
    rowId: string | null;
    alias: string | null;
  } | null>(null);
  // Keep one settled primary response apart from the selection-card query. Its
  // identity includes the workspace, canvas and URL row, so it cannot be reused
  // on another canvas or for a different record.
  const [settledPrimary, setSettledPrimary] = useState<{
    workspaceId: string;
    canvasId: number | string;
    rowId: string | null;
    data: CanvasData;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const queryClient = useQueryClient();
  const authFetchRef = useRef(authFetch);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchData = useCallback(async () => {
    if (!workspaceId || canvasId === null) {
      setLoading(false);
      setData(null);
      setLoadedFor(null);
      return;
    }

    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    try {
      setLoading(true);

      const url = `${CANVAS_API_BASE}/${canvasId}/data`;

      const response = await authFetchRef.current(url, "fetch canvas data", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getWorkspaceHeaders(workspaceId),
        },
        body: JSON.stringify({
          record_id: rowId || null,
          ...(dataSourceAlias ? { data_source_alias: dataSourceAlias } : {}),
          page: 1,
          page_size: 20,
        }),
      });
      if (!signal.aborted) {
        setData(response);
        if (!dataSourceAlias) {
          setSettledPrimary({
            workspaceId,
            canvasId,
            rowId: rowId ? String(rowId) : null,
            data: response,
          });
        }
        setLoadedFor({
          rowId: rowId ? String(rowId) : null,
          alias: dataSourceAlias || null,
        });
        setError(null);
      }
    } catch (err) {
      if (!signal.aborted) {
        console.error("Canvas data fetch error:", err);
        const message =
          err instanceof Error ? err.message : "Failed to fetch canvas data";
        setError(message);
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [workspaceId, canvasId, rowId, dataSourceAlias]);

  useEffect(() => {
    fetchData();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchData]);

  useEffect(() => {
    let subscribed = true;
    const unsubscribe = subscribeWorkspaceObjectChanges(
      workspaceId,
      (change) => {
        if (workspaceChangeAffects(change, ["canvas"], canvasId)) {
          fetchData();
          return;
        }
        const canvas = data?.canvas as {
          data_sources?: Array<{
            type?: string;
            view_id?: string | number;
            schema_id?: string | number;
            alias?: string;
            name?: string;
            id?: string | number;
            is_primary?: boolean;
          }>;
        } | null;
        const sources = canvas?.data_sources ?? [];
        const source = dataSourceAlias
          ? sources.find(
              (candidate) =>
                String(
                  candidate.alias || candidate.name || candidate.id || "",
                ) === dataSourceAlias,
            )
          : sources.find((candidate) => candidate.is_primary) || sources[0];
        if (!source) return;

        const directlyAffected = change.changedObjects.some((object) => {
          if (object.type === "view" || object.type === "row") {
            return (
              source.type === "view" &&
              String(source.view_id ?? "") === (object.viewId ?? object.id)
            );
          }
          if (object.type === "table") {
            return (
              source.type === "schema" &&
              String(source.schema_id ?? "") === object.id
            );
          }
          return false;
        });
        if (directlyAffected) {
          fetchData();
          return;
        }

        const changedTableIds = change.changedObjects
          .filter((object) => object.type === "table")
          .map((object) => object.id);
        if (
          !changedTableIds.length ||
          source.type !== "view" ||
          source.view_id == null
        )
          return;

        void loadViewDefinitionForObjectChange(
          queryClient,
          workspaceId,
          String(source.view_id),
          (viewId) =>
            createViewService(authFetchRef.current, workspaceId).getView(
              viewId,
            ),
        )
          .then((view) => {
            if (
              subscribed &&
              changedTableIds.some((tableId) => viewUsesTable(view, tableId))
            ) {
              fetchData();
            }
          })
          .catch(() => {
            // A failed metadata lookup must not break other change subscribers.
          });
      },
    );
    return () => {
      subscribed = false;
      unsubscribe();
    };
  }, [workspaceId, canvasId, data, dataSourceAlias, fetchData, queryClient]);

  return {
    data,
    loadedFor,
    primaryData:
      !dataSourceAlias &&
      settledPrimary?.workspaceId === workspaceId &&
      settledPrimary.canvasId === canvasId &&
      settledPrimary.rowId === (rowId ? String(rowId) : null)
        ? settledPrimary.data
        : null,
    loading,
    error,
    refresh: fetchData,
  };
};

/**
 * Hook for canvas mutations (create, update, delete).
 */
export const useCanvasMutations = (workspaceId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();

  const createCanvas = useCallback(
    async (data: CanvasCreate): Promise<Canvas> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(CANVAS_API_BASE, "create canvas", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getWorkspaceHeaders(workspaceId),
          },
          body: JSON.stringify(data),
        });
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to create canvas";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  const updateCanvas = useCallback(
    async (canvasId: number | string, data: CanvasUpdate): Promise<Canvas> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(
          `${CANVAS_API_BASE}/${canvasId}`,
          "update canvas",
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              ...getWorkspaceHeaders(workspaceId),
            },
            body: JSON.stringify(data),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to update canvas";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  const deleteCanvas = useCallback(
    async (canvasId: number | string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await authFetch(`${CANVAS_API_BASE}/${canvasId}`, "delete canvas", {
          method: "DELETE",
          headers: getWorkspaceHeaders(workspaceId),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to delete canvas";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  return {
    createCanvas,
    updateCanvas,
    deleteCanvas,
    loading,
    error,
  };
};

/**
 * Fetch a selected record through the existing view-row read route.
 */
export const useViewRow = (
  workspaceId: string,
  viewId?: string | number,
  rowId?: string | number,
) => {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchRow = useCallback(async () => {
    abortRef.current?.abort();
    if (!workspaceId || !viewId || !rowId) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    setData(null);
    setLoading(true);
    try {
      const response = await authFetchRef.current(
        `/api/workspace/myvault/view-rows/${encodeURIComponent(String(viewId))}/rows/${encodeURIComponent(String(rowId))}`,
        "fetch selected view row",
        { headers: getWorkspaceHeaders(workspaceId), signal },
      );
      if (!signal.aborted) {
        setData(response);
        setError(null);
      }
    } catch (err) {
      if (!signal.aborted) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch selected row",
        );
      }
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [workspaceId, viewId, rowId]);

  useEffect(() => {
    fetchRow();
    return () => abortRef.current?.abort();
  }, [fetchRow]);

  return { data, loading, error, refresh: fetchRow };
};

/**
 * Hook for canvas form submission.
 */
export const useCanvasSubmit = (workspaceId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CanvasSubmitResult | null>(null);

  const authFetch = useAuthFetch();

  const submit = useCallback(
    async (
      canvasId: number | string,
      data: Record<string, unknown>,
      rowId?: string | number,
    ): Promise<CanvasSubmitResult> => {
      setLoading(true);
      setError(null);
      setResult(null);

      try {
        const url = `${CANVAS_API_BASE}/${canvasId}/submit`;

        const response = await authFetch(url, "submit canvas", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getWorkspaceHeaders(workspaceId),
          },
          body: JSON.stringify(rowId ? { ...data, record_id: rowId } : data),
        });

        setResult(response);
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to submit canvas";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  const reset = useCallback(() => {
    setError(null);
    setResult(null);
  }, []);

  return {
    submit,
    loading,
    error,
    result,
    reset,
  };
};

// ============================================================================
// Canvas Embedding Types
// ============================================================================

/**
 * Canvas embedding definition - links a parent canvas to a child canvas.
 */
export interface CanvasEmbedding {
  id: string | number;
  parent_canvas_id: string | number;
  child_canvas_id: string | number;
  parent_data_source_alias: string;
  parent_key_field: string;
  child_data_source_alias: string;
  child_filter_field: string;
  element_id?: string;
  display_mode?: "form" | "list" | "auto";
  config_json?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface CanvasEmbeddingCreate {
  child_canvas_id: string | number;
  parent_data_source_alias: string;
  parent_key_field: string;
  child_data_source_alias: string;
  child_filter_field: string;
  element_id?: string;
  display_mode?: "form" | "list" | "auto";
  config_json?: Record<string, unknown>;
}

export interface CanvasEmbeddingUpdate {
  parent_data_source_alias?: string;
  parent_key_field?: string;
  child_data_source_alias?: string;
  child_filter_field?: string;
  display_mode?: "form" | "list" | "auto";
  config_json?: Record<string, unknown>;
}

/**
 * Available canvas for embedding (excludes current canvas and ancestors).
 */
export interface AvailableCanvasForEmbedding {
  id: string | number;
  name: string;
  description?: string;
  canvas_type: string;
  data_sources: Array<{
    alias: string;
    view_id: string | number;
    view_name?: string;
  }>;
}

// ============================================================================
// Canvas Embedding Hooks
// ============================================================================

/**
 * Hook to fetch embeddings for a canvas.
 */
export const useCanvasEmbeddings = (
  workspaceId: string,
  canvasId: string | number | null,
) => {
  const [embeddings, setEmbeddings] = useState<CanvasEmbedding[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);
  const fetchedRef = useRef(false);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchEmbeddings = useCallback(async () => {
    if (!workspaceId || !canvasId) {
      setEmbeddings([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await authFetchRef.current(
        `${CANVAS_API_BASE}/${canvasId}/embeddings`,
        "fetch canvas embeddings",
        { headers: getWorkspaceHeaders(workspaceId) },
      );
      setEmbeddings(response.items || response || []);
      setError(null);
    } catch (err) {
      console.error("Canvas embeddings fetch error:", err);
      const message =
        err instanceof Error ? err.message : "Failed to fetch embeddings";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, canvasId]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchEmbeddings();
    return () => {
      fetchedRef.current = false;
    };
  }, [fetchEmbeddings]);

  return {
    embeddings,
    loading,
    error,
    refresh: fetchEmbeddings,
  };
};

/**
 * Hook to fetch available canvases for embedding.
 */
export const useAvailableCanvasesForEmbedding = (
  workspaceId: string,
  canvasId: string | number | null,
) => {
  const [canvases, setCanvases] = useState<AvailableCanvasForEmbedding[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);
  const fetchedRef = useRef(false);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchAvailable = useCallback(async () => {
    if (!workspaceId || !canvasId) {
      setCanvases([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await authFetchRef.current(
        `${CANVAS_API_BASE}/${canvasId}/embeddings/available`,
        "fetch available canvases for embedding",
        { headers: getWorkspaceHeaders(workspaceId) },
      );
      setCanvases(response.items || response || []);
      setError(null);
    } catch (err) {
      console.error("Available canvases fetch error:", err);
      const message =
        err instanceof Error
          ? err.message
          : "Failed to fetch available canvases";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, canvasId]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchAvailable();
    return () => {
      fetchedRef.current = false;
    };
  }, [fetchAvailable]);

  return {
    canvases,
    loading,
    error,
    refresh: fetchAvailable,
  };
};

/**
 * Hook for canvas embedding mutations (create, update, delete).
 */
export const useCanvasEmbeddingMutations = (workspaceId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();

  const createEmbedding = useCallback(
    async (
      canvasId: string | number,
      data: CanvasEmbeddingCreate,
    ): Promise<CanvasEmbedding> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(
          `${CANVAS_API_BASE}/${canvasId}/embeddings`,
          "create canvas embedding",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...getWorkspaceHeaders(workspaceId),
            },
            body: JSON.stringify(data),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to create embedding";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  const updateEmbedding = useCallback(
    async (
      canvasId: string | number,
      embeddingId: string | number,
      data: CanvasEmbeddingUpdate,
    ): Promise<CanvasEmbedding> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(
          `${CANVAS_API_BASE}/${canvasId}/embeddings/${embeddingId}`,
          "update canvas embedding",
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              ...getWorkspaceHeaders(workspaceId),
            },
            body: JSON.stringify(data),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to update embedding";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  const deleteEmbedding = useCallback(
    async (
      canvasId: string | number,
      embeddingId: string | number,
    ): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await authFetch(
          `${CANVAS_API_BASE}/${canvasId}/embeddings/${embeddingId}`,
          "delete canvas embedding",
          {
            method: "DELETE",
            headers: getWorkspaceHeaders(workspaceId),
          },
        );
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to delete embedding";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  return {
    createEmbedding,
    updateEmbedding,
    deleteEmbedding,
    loading,
    error,
  };
};
